import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useOrganizationList, SignOutButton } from '@clerk/react';
import { LayoutDashboard, UserCircle, Clock, CalendarOff } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import { useClerkReady } from '../../lib/useClerkReady';
import AuthUnavailable from '../../components/AuthUnavailable';
import SignInPrompt from '../../components/SignInPrompt';

/**
 * Polling delay + total budget for the "staff row not yet provisioned"
 * state. The invitation-accepted webhook usually lands inside 500ms, but a
 * slow Clerk delivery or a brief D1 hiccup can push it to a few seconds.
 * 15 attempts × 2s = 30s ceiling before we give up and ask for a refresh.
 */
const SETUP_POLL_INTERVAL_MS = 2000;
const SETUP_POLL_MAX_ATTEMPTS = 15;

type SetupState =
  | 'unknown'
  | 'ready'
  | 'provisioning'
  | 'failed'
  | 'inactive'
  | 'not_bookable';

export default function StaffLayout() {
  const { isLoaded, isSignedIn, orgId, orgRole, timedOut } = useClerkReady();
  const list = useOrganizationList({ userMemberships: true });
  const apiFn = useAuthedApi();
  const { search } = useLocation();

  // Mirror the AdminLayout fix: if signed in with memberships but no active
  // org, flip the first one active so the JWT carries org_id.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (orgId) return;
    if (!list.isLoaded || !list.setActive) return;
    const memberships = list.userMemberships?.data ?? [];
    if (memberships.length === 0) return;
    const first = memberships[0];
    if (!first) return;
    list.setActive({ organization: first.organization.id }).catch((err) => {
      console.warn('[staff-layout] setActive failed', err);
    });
  }, [isLoaded, isSignedIn, orgId, list.isLoaded, list.userMemberships?.data, list.setActive]);

  // Polling for the staff row. A freshly-invited user can land here before
  // the organizationInvitation.accepted webhook has created their row.
  // On 404, retry every 2s up to 30s; on 2xx, render the dashboard; on any
  // other error, give up the poll and let the inner page surface the issue.
  //
  // Special case: an admin who is NOT also a bookable staff member (org
  // creator who doesn't take bookings themselves). There's no provisioning in
  // flight — they just haven't opted in — so instead of polling or bouncing
  // them to /admin, we show a prompt right here to turn on bookings.
  const [setupState, setSetupState] = useState<SetupState>('unknown');
  // Bump to re-run the /staff/me check without a full reload (e.g. after an
  // admin enables bookings from the prompt below).
  const [reloadKey, setReloadKey] = useState(0);
  const attemptRef = useRef(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const isAdmin = orgRole === 'org:admin';

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !orgId) return;

    let cancelled = false;
    attemptRef.current = 0;

    const check = async () => {
      if (cancelled) return;
      try {
        await apiFn('/staff/me');
        if (!cancelled) {
          setSetupState('ready');
          setAttemptCount(0);
        }
      } catch (e) {
        if (cancelled) return;
        const err = e as Error & { status?: number; body?: { error?: string } };
        const status = err.status;
        if (status === 404) {
          if (isAdmin) {
            // Admin with no active staff row — they're just not opted into
            // bookings (a missing row, or one they deactivated). No
            // provisioning race, so don't poll; offer the enable prompt.
            setSetupState('not_bookable');
            return;
          }
          // An inactive row is a settled state, not a race — the account was
          // deactivated (usually: removed from the org). Never poll; show the
          // explanation immediately.
          if (err.body?.error === 'staff_inactive') {
            setSetupState('inactive');
            return;
          }
          attemptRef.current += 1;
          setAttemptCount(attemptRef.current);
          if (attemptRef.current >= SETUP_POLL_MAX_ATTEMPTS) {
            setSetupState('failed');
          } else {
            setSetupState('provisioning');
            setTimeout(check, SETUP_POLL_INTERVAL_MS);
          }
        } else {
          // Non-404 errors are not "still being created" — let pages handle.
          setSetupState('ready');
        }
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, orgId, apiFn, isAdmin, reloadKey]);

  if (!isLoaded) return timedOut ? <AuthUnavailable /> : <p className="muted">Loading…</p>;

  if (!isSignedIn) {
    // Invitation links from Clerk land here with __clerk_ticket / __clerk_status
    // query params. When the invited email is NEW, Clerk sets
    // __clerk_status=sign_up; when the email ALREADY has a Clerk account it
    // sets sign_in (accept-by-signing-in). Route by status — forwarding a
    // sign_in ticket to /sign-up makes Clerk reject it ("you already have an
    // account"), the ticket is never consumed, and the invitee can't join.
    const params = new URLSearchParams(search);
    if (params.has('__clerk_ticket')) {
      const dest = params.get('__clerk_status') === 'sign_in' ? '/sign-in' : '/sign-up';
      return <Navigate to={`${dest}${search}`} replace />;
    }
    return (
      <SignInPrompt
        title="Sign in to your dashboard"
        message="Sign in to see your appointments, availability, and calendar connections."
      />
    );
  }

  if (!orgId) {
    if (!list.isLoaded) return <p className="muted">Loading…</p>;
    const memberships = list.userMemberships?.data ?? [];
    if (memberships.length === 0) return <Navigate to="/welcome" replace />;
    return <p className="muted">Activating your organization…</p>;
  }

  if (setupState === 'unknown') {
    return <p className="muted">Loading…</p>;
  }

  if (setupState === 'not_bookable') {
    return (
      <NotBookable
        onEnabled={() => {
          setSetupState('unknown');
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  if (setupState === 'provisioning') {
    return <SetupState attempt={attemptCount} />;
  }

  if (setupState === 'inactive') {
    return <Deactivated />;
  }

  if (setupState === 'failed') {
    return <SetupFailed />;
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `tab${isActive ? ' active' : ''}`;

  return (
    <>
      <nav className="tabs" aria-label="Dashboard sections">
        <NavLink to="dashboard" className={tabClass}>
          <LayoutDashboard size={14} /> Dashboard
        </NavLink>
        <NavLink to="profile" className={tabClass}>
          <UserCircle size={14} /> Profile
        </NavLink>
        <NavLink to="availability" className={tabClass}>
          <Clock size={14} /> Availability
        </NavLink>
        <NavLink to="time-off" className={tabClass}>
          <CalendarOff size={14} /> Time off
        </NavLink>
      </nav>
      <Outlet />
    </>
  );
}

function SetupState({ attempt }: { attempt: number }) {
  // Progressive copy so the user doesn't feel like they're stuck looking at
  // the same sentence for thirty seconds. Numbers tuned to the 2s interval.
  let message: string;
  if (attempt < 4) {
    message = 'Hang tight — we&rsquo;re finishing setting up your account. This usually only takes a moment.';
  } else if (attempt < 9) {
    message = 'Still finalizing things. Almost there.';
  } else {
    message = 'This is taking a little longer than usual. Sit tight — we&rsquo;re still working on it.';
  }

  return (
    <div className="setup-state">
      <div className="setup-spinner" aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
      <h2>Setting up your account</h2>
      <p dangerouslySetInnerHTML={{ __html: message }} />
    </div>
  );
}

function SetupFailed() {
  return (
    <div className="setup-state">
      <h2>We couldn&rsquo;t find your staff profile</h2>
      <p>
        If you were just invited, your account may still be provisioning &mdash;
        refresh in a minute. If that doesn&rsquo;t help, you may not be set up as
        a bookable staff member yet. Ask your organization&rsquo;s admin to invite
        you (or reactivate you) from their Staff settings.
      </p>
      <div className="row" style={{ justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
        <button className="primary" onClick={() => window.location.reload()}>
          Refresh
        </button>
        <SignOutButton>
          <button type="button">Sign out</button>
        </SignOutButton>
      </div>
    </div>
  );
}

/**
 * Shown to an admin who isn't a bookable staff member. Rather than bouncing
 * them to /admin, we let them opt in right here: enter the name customers
 * should see and turn on bookings. On success the parent re-checks /staff/me
 * and renders the real dashboard. The display name is entered explicitly (never
 * pulled from Clerk) so the booking page never shows the company name.
 */
function NotBookable({ onEnabled }: { onEnabled: () => void }) {
  const apiFn = useAuthedApi();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function enable() {
    if (!name.trim()) {
      setErr('Enter the name customers should see.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await apiFn('/staff/me/enable-booking', { method: 'POST', body: { name: name.trim() } });
      onEnabled();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <div className="setup-state">
      <h2>You&rsquo;re not taking bookings</h2>
      <p>
        Admin accounts aren&rsquo;t bookable by default. If you also meet with
        customers, turn this on to appear as a bookable staff member with your
        own calendar and availability.
      </p>
      <div style={{ maxWidth: '20rem', margin: '1.25rem auto 0', textAlign: 'left' }}>
        <label>
          <span>Your booking name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sarah Lee"
            maxLength={120}
          />
        </label>
        {err && <p className="err">{err}</p>}
      </div>
      <div className="row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
        <button className="primary" onClick={enable} disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : 'Start taking bookings'}
        </button>
      </div>
    </div>
  );
}

/**
 * Shown when the caller has a staff row but it's deactivated (is_active=0).
 * The common cause is being removed from the Clerk organization, which the
 * membership.deleted webhook flips to inactive — so this state can appear
 * without anyone deliberately "deactivating" the person in-app. Explain that
 * plainly rather than leaving them staring at a failed provisioning spinner.
 */
function Deactivated() {
  return (
    <div className="setup-state">
      <h2>Your access is inactive</h2>
      <p>
        Your staff profile at this organization has been deactivated &mdash;
        usually because you were removed from the organization, or an admin
        turned your access off. Your bookings and history are safe.
      </p>
      <p>
        To get back in, ask your organization&rsquo;s admin to reactivate or
        re-invite you from their Staff settings.
      </p>
      <div className="row" style={{ justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
        <button className="primary" onClick={() => window.location.reload()}>
          Refresh
        </button>
        <SignOutButton>
          <button type="button">Sign out</button>
        </SignOutButton>
      </div>
    </div>
  );
}
